// src/components/Auth/RecoverSubscription.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://http://https://southamerica-east1-drecenna.cloudfunctions.net/api';

const RecoverSubscription = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [userExists, setUserExists] = useState(false);
  const navigate = useNavigate();

  // Aplicar dark theme quando o componente montar
  useEffect(() => {
    // Aplicar dark theme ao root para garantir que o tema se aplique a tudo
    document.documentElement.classList.add('dark-theme');
    
    // Cleanup function para remover a classe quando o componente for desmontado
    return () => {
      document.documentElement.classList.remove('dark-theme');
    };
  }, []);

  const handleRecovery = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Por favor, informe o email usado no pagamento.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');
    setUserExists(false);

    try {
      // Primeiro, usar o endpoint "simples" que verificaria pendingAccounts
      const response = await axios.post(`${API_URL}/recover-subscription`, { email });
      
      // Se for um usuário que já tem conta
      if (response.data.alreadyRegistered) {
        setUserExists(true);
        setMessage(response.data.message || 'Você já possui uma conta registrada. Por favor, faça login.');
        
        // Dar tempo para o usuário ler a mensagem antes de redirecionar
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: response.data.message || 'Você já possui uma conta registrada. Por favor, faça login.' 
            } 
          });
        }, 3000);
        
        return;
      }
      
      // Se não encontrou nada, tentar uma busca mais profunda
      if (!response.data.found) {
        setMessage('Buscando informações no Stripe, isso pode levar alguns segundos...');
        const stripeResponse = await axios.post(`${API_URL}/check-payments-by-email`, { email });
        
        if (stripeResponse.data.found) {
          // Se encontrou no Stripe, redirecionar com o sessionId
          navigate(`/signup-success?session_id=${stripeResponse.data.sessionId || 'manual-recovery'}`);
          return;
        }
        
        // Se não encontrou nem no Stripe
        setError(
          'Não encontramos nenhuma assinatura para este email. ' +
          'Verifique se digitou o mesmo email usado durante o pagamento ou use a recuperação manual.'
        );
      } else {
        // Se encontrou, redirecionar para a página de conclusão de cadastro
        navigate(`/signup-success?session_id=${response.data.sessionId}`);
      }
    } catch (error) {
      console.error('Erro ao recuperar assinatura:', error);
      setError(
        'Ocorreu um erro ao verificar sua assinatura. ' +
        'Tente novamente ou entre em contato com nosso suporte.'
      );
    } finally {
      setLoading(false);
      // Manter a mensagem de "usuário existe" se for o caso
      if (!userExists) {
        setMessage('');
      }
    }
  };

  return (
    <div className="auth-container dark-theme">
      {/* Formas decorativas para o background */}
      <div className="shape shape-1"></div>
      <div className="shape shape-2"></div>
      <div className="shape shape-3"></div>
      
      <div className="auth-card">
        <div className="auth-logo">
          <div className="logo-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </div>
          <h2>Recuperar Assinatura</h2>
        </div>
        
        <p className="auth-instructions">Se você já realizou o pagamento mas não completou o cadastro, informe o email usado durante o pagamento.</p>
        
        {message && <div className="info-message animate-fade-in">{message}</div>}
        {error && <div className="error-message animate-fade-in">{error}</div>}
        
        <form onSubmit={handleRecovery} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">
              <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              Email usado no pagamento
            </label>
            <div className="input-container">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu email"
                disabled={loading || userExists}
                className="input-with-icon"
              />
            </div>
          </div>
          
          <button type="submit" className="login-button" disabled={loading || userExists}>
            {loading ? (
              <>
                <span className="spinner"></span>
                <span>Verificando...</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="8 12 12 16 16 12"></polyline>
                  <line x1="12" y1="8" x2="12" y2="16"></line>
                </svg>
                <span>Recuperar Assinatura</span>
              </>
            )}
          </button>
        </form>
        
        <div className="auth-links">
          {userExists ? (
            <p className="auth-link">
              <Link to="/login" className="login-link">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                  <polyline points="10 17 15 12 10 7"></polyline>
                  <line x1="15" y1="12" x2="3" y2="12"></line>
                </svg>
                Ir para página de login
              </Link>
            </p>
          ) : (
            <>
              <p className="auth-link">
                <Link to="/" className="signup-link">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Ainda não fez o pagamento? Ver planos
                </Link>
              </p>
              <p className="auth-link">
                <Link to="/login">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                    <polyline points="10 17 15 12 10 7"></polyline>
                    <line x1="15" y1="12" x2="3" y2="12"></line>
                  </svg>
                  Já tem uma conta? Fazer login
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecoverSubscription;